export const technologies = {
  breadcrumb: {
    home: 'Home',
    technologies: 'Technologies',
  },
  hero: {
    title: 'Filtration Technologies',
    subtitle: 'Comprehensive portfolio of advanced separation and purification technologies for diverse industrial applications',
  },
  sections: {
    reverseOsmosis: {
      name: 'Reverse Osmosis',
      description: 'High-pressure membrane filtration process that removes dissolved solids, ions, and contaminants from water and industrial fluids through semi-permeable membranes.',
      howItWorksTitle: 'How It Works',
      howItWorks: [
        'Feed water is pressurized to 15-70 bar',
        'Water molecules pass through semi-permeable membrane',
        'Dissolved solids and contaminants are rejected',
        'Purified permeate is collected',
        'Concentrate stream is discharged or recycled',
      ],
      applicationsTitle: 'Industrial Applications',
      appDesalination: 'Desalination',
      appDesalinationDesc: 'Seawater and brackish water treatment',
      appUltraPureWater: 'Ultra-Pure Water',
      appUltraPureWaterDesc: 'Pharmaceutical and electronics industries',
      appFoodBeverage: 'Food & Beverage',
      appFoodBeverageDesc: 'Process water and ingredient concentration',
      appWastewater: 'Wastewater',
      appWastewaterDesc: 'Water recovery and reuse',
      advantagesTitle: 'Key Advantages',
      advantages: [
        'Removes 95-99% of dissolved solids',
        'Produces high-purity water',
        'Compact system footprint',
        'Automated operation',
        'Low chemical consumption',
        'Consistent product quality',
      ],
      specsTitle: 'Technical Specifications',
      specParticleRemoval: 'Particle Removal',
      specOperatingPressure: 'Operating Pressure',
      specRejectionRate: 'Rejection Rate',
      specTempRange: 'Temperature Range',
    },
    nanofiltration: {
      name: 'Nanofiltration',
      description: 'Selective membrane filtration technology that separates dissolved molecules and multivalent ions while allowing monovalent ions to pass through.',
      howItWorks: [
        'Feed solution is pressurized to 5-40 bar',
        'Nanofiltration membrane selectively separates molecules',
        'Multivalent ions and organic molecules are retained',
        'Monovalent ions partially pass through',
        'Permeate and concentrate streams are collected',
      ],
      appWaterSoftening: 'Water Softening',
      appWaterSofteningDesc: 'Removal of hardness and sulfates',
      appColorRemoval: 'Color Removal',
      appColorRemovalDesc: 'Textile and dye industry applications',
      appPharmaceutical: 'Pharmaceutical',
      appPharmaceuticalDesc: 'API purification and concentration',
      appEnvironmental: 'Environmental',
      appEnvironmentalDesc: 'Heavy metal removal',
      advantages: [
        'Selective ion separation',
        'Lower operating pressure than RO',
        'Reduced energy consumption',
        'Partial demineralization',
        'Organic compound removal',
        'Cost-effective operation',
      ],
    },
    ultrafiltration: {
      name: 'Ultrafiltration',
      description: 'Membrane-based separation process that removes macromolecules, colloids, bacteria, and suspended particles from solutions using porous membranes.',
      howItWorks: [
        'Feed solution flows through membrane modules',
        'Pressure differential drives separation (1-10 bar)',
        'Macromolecules and particles are retained',
        'Water and dissolved solids pass through',
        'Backwashing maintains membrane performance',
      ],
      appFoodProcessing: 'Food Processing',
      appFoodProcessingDesc: 'Protein concentration and clarification',
      appBiotechnology: 'Biotechnology',
      appBiotechnologyDesc: 'Cell harvesting and protein recovery',
      appWaterTreatment: 'Water Treatment',
      appWaterTreatmentDesc: 'Drinking water and wastewater treatment',
      appDairyIndustry: 'Dairy Industry',
      appDairyIndustryDesc: 'Milk and whey processing',
      advantages: [
        'Removes bacteria and viruses',
        'Low operating pressure',
        'High flux rates',
        'Minimal chemical usage',
        'Scalable systems',
        'Long membrane life',
      ],
    },
    microfiltration: {
      name: 'Microfiltration',
      description: 'Low-pressure membrane filtration that removes bacteria, suspended solids, and large particles for clarification and sterilization applications.',
      howItWorks: [
        'Feed flows through microporous membranes',
        'Low pressure differential (0.1-3 bar) applied',
        'Bacteria and particles are retained',
        'Clarified liquid passes through',
        'Crossflow or dead-end filtration modes',
      ],
      appBeverageClarification: 'Beverage Clarification',
      appBeverageClarificationDesc: 'Beer, wine, and juice processing',
      appSterileFiltration: 'Sterile Filtration',
      appSterileFiltrationDesc: 'Pharmaceutical and biotech applications',
      appCellSeparation: 'Cell Separation',
      appCellSeparationDesc: 'Biotechnology and research',
      appWaterTreatment: 'Water Treatment',
      appWaterTreatmentDesc: 'Pre-treatment and polishing',
      advantages: [
        'Effective bacteria removal',
        'Very low operating pressure',
        'High throughput rates',
        'Cold sterilization capability',
        'Minimal product degradation',
        'Easy operation and maintenance',
      ],
    },
    coarseFiltration: {
      name: 'Coarse Filtration',
      description: 'Pre-filtration process that removes large particles and suspended solids to protect downstream equipment and improve overall process efficiency.',
      howItWorks: [
        'Feed enters filter housing or vessel',
        'Large particles are captured by filter media',
        'Clarified liquid flows through',
        'Backwashing removes accumulated solids',
        'Continuous or batch operation modes',
      ],
      appPretreatment: 'Pre-treatment',
      appPretreatmentDesc: 'Protection of fine filtration systems and media',
      appSedimentRemoval: 'Sediment Removal',
      appSedimentRemovalDesc: 'Raw water and process streams',
      appEquipmentProtection: 'Equipment Protection',
      appEquipmentProtectionDesc: 'Pumps, valves, and instruments',
      appProcessWater: 'Process Water',
      appProcessWaterDesc: 'Industrial water conditioning',
      advantages: [
        'Low capital cost',
        'Simple operation',
        'High dirt-holding capacity',
        'Protects downstream equipment',
        'Reduces maintenance costs',
        'Extends fine filter life',
      ],
    },
    filtrationMedia: {
      name: 'Filtration Media',
      description: 'Advanced metallic and composite polymer filtration media designed for specific applications, offering superior performance and durability.',
      howItWorks: [
        'Media selected based on application requirements',
        'Particles captured through depth or surface filtration',
        'Media structure optimized for flow and retention',
        'Cleanable or disposable options available',
        'Custom media formulations for specific needs',
      ],
      appOilGas: 'Oil & Gas',
      appOilGasDesc: 'Process filtration and separation',
      appChemicalProcessing: 'Chemical Processing',
      appChemicalProcessingDesc: 'Catalyst recovery and product filtration',
      appFoodIndustry: 'Food Industry',
      appFoodIndustryDesc: 'Clarification and sterilization',
      appIndustrial: 'Industrial',
      appIndustrialDesc: 'General process filtration',
      advantages: [
        'Wide range of materials',
        'High temperature resistance',
        'Chemical compatibility',
        'Reusable options available',
        'Customizable properties',
        'Long service life',
      ],
      specs: {
        materials: 'Materials',
        poreSizes: 'Pore Sizes',
        tempRange: 'Temperature Range',
        chemicalResistance: 'Chemical Resistance',
      },
    },
  },
  cta: {
    title: 'Need Help Selecting the Right Technology?',
    subtitle: 'Our technical experts can help you choose the optimal filtration solution for your specific application',
    requestConsultation: 'Request Technology Consultation',
  },
};